/**
 * The header's three-dot menu for a consumer, holding "Book your free 15-min session" and the "Planned Summary" shortcut
 * to Your Plan so they don't crowd the header. The trigger is the same circle as the account button beside it. Hidden
 * when there's nothing to offer.
 */
import { CalendarDays, ClipboardList, EllipsisVertical } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@design-system';
import { useConsumerHeaderMenu } from '../hooks/use-consumer-header-menu';
import { LTC_PLANNING_SESSION_LABEL, LTC_PLANNING_SESSION_URL } from '../lib/planning-session';

const ITEM_CLASS = 'min-h-10 gap-3 px-3 text-gray-950 [&_svg]:size-4';

export function ConsumerHeaderMenu({ clientId }: { clientId: string | null | undefined }) {
  const { showPlanningSession, yourPlanTarget } = useConsumerHeaderMenu(clientId);
  if (!showPlanningSession && !yourPlanTarget) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className="group flex min-h-11 items-center rounded-full p-2"
        aria-label="More options"
        data-testid="consumer-header-menu"
      >
        <span className="flex size-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-950 group-data-[state=open]:border-primary">
          <EllipsisVertical size={18} />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-72 rounded-xl p-1"
        align="end"
        side="bottom"
        sideOffset={5}
      >
        {showPlanningSession && (
          <DropdownMenuItem
            asChild
            className={ITEM_CLASS}
          >
            <a
              href={LTC_PLANNING_SESSION_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CalendarDays />
              {LTC_PLANNING_SESSION_LABEL}
            </a>
          </DropdownMenuItem>
        )}
        {yourPlanTarget && (
          <DropdownMenuItem
            asChild
            className={ITEM_CLASS}
          >
            <Link
              to={yourPlanTarget}
              data-testid="your-plan-shortcut"
            >
              <ClipboardList />
              Planned Summary
            </Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
